import React from 'react';
import { Building2, Users, GraduationCap, BookOpen, TrendingUp, TrendingDown } from 'lucide-react';
import type { DashboardStats } from '../../../services/dashboardService';

interface DetailedStatsProps {
  stats: DashboardStats;
}

const DetailedStats: React.FC<DetailedStatsProps> = ({ stats }) => {
  // Chưa có bản ghi nào thì total = 0; chia thẳng sẽ ra NaN%
  const activePercent = (active: number, total: number) => (total > 0 ? (active / total) * 100 : 0);

  const getStatusColor = (active: number, total: number) => {
    const percentage = activePercent(active, total);
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusIcon = (active: number, total: number) => {
    const percentage = activePercent(active, total);
    if (percentage >= 90) return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (percentage >= 70) return <TrendingUp className="h-4 w-4 text-yellow-600" />;
    return <TrendingDown className="h-4 w-4 text-red-600" />;
  };

  const statItems = [
    {
      title: 'Trường học',
      icon: Building2,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      active: stats.schools.active,
      total: stats.schools.total,
      inactive: stats.schools.inactive,
      pending: stats.schools.pending,
      // Dữ liệu cũ thiếu status hoặc mang giá trị lạ không thuộc dòng nào ở trên
      unknown: Math.max(0, stats.schools.total - stats.schools.active - stats.schools.inactive - stats.schools.pending)
    },
    {
      title: 'Lớp học',
      icon: BookOpen,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      active: stats.classes.active,
      total: stats.classes.total,
      inactive: stats.classes.inactive,
      archived: stats.classes.archived
    },
    {
      title: 'Giáo viên',
      icon: GraduationCap,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      active: stats.teachers.active,
      total: stats.teachers.total,
      inactive: stats.teachers.inactive
    },
    {
      title: 'Học sinh',
      icon: Users,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      active: stats.students.active,
      total: stats.students.total,
      inactive: stats.students.inactive
    }
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Thống kê chi tiết</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {statItems.map((item) => {
          const Icon = item.icon;
          const percentage = Math.round(activePercent(item.active, item.total));
          
          return (
            <div key={item.title} className={`p-4 rounded-lg border ${item.borderColor} ${item.bgColor}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <div className={`p-2 rounded-lg ${item.bgColor}`}>
                    <Icon className={`h-5 w-5 ${item.color}`} />
                  </div>
                  <h4 className="font-medium text-gray-900 ml-3">{item.title}</h4>
                </div>
                <div className="flex items-center">
                  {getStatusIcon(item.active, item.total)}
                  <span className={`ml-1 text-sm font-medium ${getStatusColor(item.active, item.total)}`}>
                    {percentage}%
                  </span>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tổng số:</span>
                  <span className="font-medium text-gray-900">
                    {item.title === 'Học sinh' ? item.total.toLocaleString() : item.total}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-green-600">Hoạt động:</span>
                  <span className="font-medium text-green-600">
                    {item.title === 'Học sinh' ? item.active.toLocaleString() : item.active}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-red-600">Không hoạt động:</span>
                  <span className="font-medium text-red-600">
                    {item.title === 'Học sinh' ? item.inactive.toLocaleString() : item.inactive}
                  </span>
                </div>
                {item.pending !== undefined && (
                  <div className="flex justify-between text-sm">
                    <span className="text-yellow-600">Chờ duyệt:</span>
                    <span className="font-medium text-yellow-600">{item.pending}</span>
                  </div>
                )}
                {item.archived !== undefined && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Đã lưu trữ:</span>
                    <span className="font-medium text-gray-600">{item.archived}</span>
                  </div>
                )}
                {!!item.unknown && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Chưa rõ trạng thái:</span>
                    <span className="font-medium text-gray-600">{item.unknown}</span>
                  </div>
                )}
              </div>
              
              {/* Progress bar */}
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      percentage >= 90 ? 'bg-green-500' : 
                      percentage >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DetailedStats;
